// Boost Blenz Admin — Insights (Overview → Insights).
//
// "Pull it when I need it" audits. NOTHING polls in the background: each card shows the
// LAST SAVED snapshot and only calls the platform when you hit Refresh — which saves a new
// snapshot to history, so you can see how the picture drifts over time.
//   • Meta / Google demographics — who BUYS (headline) vs who CLICKS, by age + gender, with
//     an optional "vs last year" trend toggle.
//   • Klaviyo timing — best day + time to send (with a top-3 send schedule) and top-5 topics.
import React from "react";
import { OSWALD, FIGTREE, useT, useIsMobile } from "../theme";
import { Icon } from "../atoms";

const C = {
  card: "linear-gradient(180deg,rgba(26,20,54,.75),rgba(18,13,40,.72))",
  cell: "#171232", line: "rgba(150,130,230,.20)", lineSoft: "rgba(150,130,230,.10)",
  ink: "#f4f1ff", body: "#e6dfff", muted: "#a99fd0", faint: "#7a6fa8",
  accent: "#f8925c", green: "#5fd39a", purple: "#8b7bf0", blue: "#6aa9e0", pink: "#ee7aa8",
};
const GENDER: Record<string, { label: string; color: string }> = {
  male: { label: "Men", color: "#6aa9e0" }, female: { label: "Women", color: "#ee7aa8" }, unknown: { label: "Unknown", color: "#6c6392" },
};
const WINDOWS = [{ id: "l30", label: "L30" }, { id: "l60", label: "L60" }, { id: "l90", label: "L90" }, { id: "ytd", label: "YTD" }];
const WIN_LABEL: Record<string, string> = { l30: "L30 (last 30 days)", l60: "L60 (last 60 days)", l90: "L90 (last 90 days)", ytd: "YTD" };

const int = (n: number) => Math.round(n || 0).toLocaleString("en-US");
const pct = (n: number) => (n || 0).toFixed(1) + "%";
const money = (n: number) => "$" + int(n || 0);
const money2 = (n: number) => "$" + (n || 0).toFixed(2);
const gv = (rows: any[], key: string, field: string) => rows?.find((r) => r.key === key)?.[field] || 0;
const sharePct = (d: any, gender: string, field: string) => (d?.totals?.[field] > 0 ? (gv(d.gender, gender, field) / d.totals[field]) * 100 : 0);
const fmtWhen = (s?: string) => {
  if (!s) return "";
  const d = new Date(s.replace(" ", "T") + (s.includes("Z") ? "" : "Z"));
  return isNaN(+d) ? s : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};
const prettyErr = (e: string) =>
  /no_meta_token/.test(e) ? "Meta isn't connected." :
  /no_klaviyo_key/.test(e) ? "Klaviyo isn't connected." :
  /no_google|no_meta_ad_account/.test(e) ? "That platform isn't connected." :
  /429|cooldown|rate/.test(e) ? "Klaviyo is rate-limited right now — try again in a few minutes." :
  "Pull failed: " + e;

// Shared snapshot store: loads the latest, refreshes (saves) on demand, browses history.
function useSnapshot(kind: string, defaultWin: string) {
  const [win, setWin] = React.useState(defaultWin);
  const [store, setStore] = React.useState<any>(null);
  const [browse, setBrowse] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const load = React.useCallback(async () => {
    setLoading(true);
    try { setStore(await (await fetch(`/api/insights/${kind}`)).json()); } catch { setErr("Couldn't load saved insights."); } finally { setLoading(false); }
  }, [kind]);
  React.useEffect(() => { load(); }, [load]);
  const refresh = async () => {
    setBusy(true); setErr("");
    try { const j = await (await fetch(`/api/insights/${kind}/refresh?window=${win}`, { method: "POST" })).json(); if (j.error) setErr(prettyErr(j.error)); else { setStore(j); setBrowse(null); } }
    catch { setErr("Refresh failed — try again."); } finally { setBusy(false); }
  };
  const pickHistory = async (id: string) => {
    if (!id || id === store?.latest?.id) { setBrowse(null); return; }
    try { setBrowse(await (await fetch(`/api/insights/${kind}/snapshot/${id}`)).json()); } catch { /* ignore */ }
  };
  return { win, setWin, store, snap: browse || store?.latest, browsing: !!browse && browse.id !== store?.latest?.id, loading, busy, err, refresh, pickHistory };
}

export function InsightsView() {
  const t = useT();
  const isMobile = useIsMobile();
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: 16 }}>
      <div>
        <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: isMobile ? 24 : 32, color: t.fg }}>Insights</div>
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5 }}>
          On-demand audits you pull when you need them. Nothing runs in the background — hit Refresh to get the latest
          and save it; older pulls stay as history so you can watch the picture shift over time.
        </div>
      </div>
      <DemoCard isMobile={isMobile} kind="meta_demo" title="Who's buying · Meta" defaultWin="l90" />
      <DemoCard isMobile={isMobile} kind="google_demo" title="Who's buying · Google" defaultWin="l90" />
      <KlaviyoCard isMobile={isMobile} />
    </div>
  );
}

// ---- shared card header (window pills + Refresh + as-of + history) -------------------
function CardHeader({ dot, title, s }: any) {
  const { snap, store, win, setWin, refresh, pickHistory, busy, browsing } = s;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: dot }} />
          <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", fontSize: 12.5, color: C.body }}>{title}</span>
        </div>
        <div style={{ fontFamily: FIGTREE, fontSize: 12, color: C.faint, marginTop: 6 }}>
          {snap ? <>Snapshot: <b style={{ color: C.muted }}>{WIN_LABEL[snap.window] || snap.window}</b> · as of {fmtWhen(snap.createdAt)}{browsing ? " · viewing history" : ""}</> : "No snapshot yet — pick a window and Refresh."}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 2, background: C.cell, border: "1px solid " + C.line, borderRadius: 999, padding: 3 }}>
          {WINDOWS.map((w) => (
            <span key={w.id} onClick={() => setWin(w.id)} style={{ cursor: "pointer", fontFamily: OSWALD, fontWeight: 700, fontSize: 11, letterSpacing: "0.05em", padding: "5px 11px", borderRadius: 999, background: win === w.id ? C.purple : "transparent", color: win === w.id ? "#160f2e" : C.muted }}>{w.label}</span>
          ))}
        </div>
        <button onClick={refresh} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 7, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, background: C.accent, color: "#160f2e", border: "none", borderRadius: 999, padding: "8px 15px", fontFamily: OSWALD, fontWeight: 700, fontSize: 11.5, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          <Icon name="refresh" size={13} color="#160f2e" />{busy ? "Pulling…" : "Refresh"}
        </button>
        {store?.history?.length > 1 && (
          <select value={snap?.id || ""} onChange={(e) => pickHistory(e.target.value)} style={{ background: C.cell, color: C.body, border: "1px solid " + C.line, borderRadius: 8, padding: "7px 9px", fontFamily: FIGTREE, fontSize: 12, maxWidth: "100%", minWidth: 0 }}>
            {store.history.map((h: any) => (
              <option key={h.id} value={h.id}>{fmtWhen(h.createdAt)} · {(WIN_LABEL[h.window] || h.window).replace(/ \(.*\)/, "")}{h.id === store.latest.id ? " (latest)" : ""}</option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

// ---- demographics card (Meta + Google share this) -----------------------------------
function DemoCard({ isMobile, kind, title, defaultWin }: any) {
  const s = useSnapshot(kind, defaultWin);
  const [showAge, setShowAge] = React.useState(false);
  const [cmp, setCmp] = React.useState(false);
  const p = s.snap?.payload;
  const noun = kind === "google_demo" ? "conversions" : "purchases";
  const showCmp = cmp && p?.prior;
  return (
    <Card>
      <CardHeader dot={kind === "google_demo" ? C.accent : C.purple} title={title} s={s} />
      {s.err && <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: "#ffb3a3", marginBottom: 12 }}>{s.err}</div>}
      {!p ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: C.muted, padding: "10px 0" }}>
          {s.loading ? "Loading the last saved snapshot…" : "Nothing saved yet. Choose a window above and hit Refresh to pull age + gender."}
        </div>
      ) : p.available === false ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: C.muted, padding: "10px 0", lineHeight: 1.6 }}>{p.headline?.text} Meta is your robust demographic source.</div>
      ) : (
        <>
          <div style={{ background: C.cell, border: "1px solid " + C.line, borderRadius: 12, padding: "16px 18px", marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
              <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10.5, color: C.faint }}>Your target market</span>
              {p.prior && <span onClick={() => setCmp((v) => !v)} style={{ cursor: "pointer", fontFamily: FIGTREE, fontSize: 11, fontWeight: 700, color: showCmp ? "#160f2e" : C.muted, background: showCmp ? C.green : "transparent", border: "1px solid " + (showCmp ? C.green : C.line), borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap" }}>{showCmp ? "Hide last year" : "vs last year"}</span>}
            </div>
            <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: isMobile ? 24 : 30, color: C.green, lineHeight: 1.1, marginTop: 5 }}>{p.headline?.text}</div>
            <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: C.muted, marginTop: 7 }}>By {noun} · {WIN_LABEL[p.window] || p.window} · {int(p.totals?.purchases)} {noun} from {int(p.totals?.clicks)} clicks</div>
            {showCmp && <Compare p={p} field="purchases" />}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0,1fr)" : "minmax(0,1fr) minmax(0,1fr)", gap: 16, marginBottom: 18 }}>
            <GenderBar title="Who buys" sub={"share of " + noun} rows={p.gender} field="purchases" total={p.totals?.purchases} prior={showCmp ? p.prior.gender : null} priorTotal={p.prior?.totals?.purchases} />
            <GenderBar title="Who we reach" sub="share of clicks" rows={p.gender} field="clicks" total={p.totals?.clicks} prior={showCmp ? p.prior.gender : null} priorTotal={p.prior?.totals?.clicks} />
          </div>
          <button onClick={() => setShowAge((v) => !v)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 9, cursor: "pointer", background: C.cell, border: "1px solid " + C.line, borderRadius: 10, padding: "11px 14px", color: C.body, fontFamily: OSWALD, fontWeight: 700, fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {showAge ? "Hide age breakdown" : "More — age breakdown"}
            <span style={{ display: "inline-block", transition: "transform .15s", transform: showAge ? "rotate(180deg)" : "none", fontSize: 15, lineHeight: 1 }}>▾</span>
          </button>
          {showAge && <AgeTable p={p} kind={kind} />}
        </>
      )}
    </Card>
  );
}

// "vs last year" summary: male-share delta + age-range shift, in plain language.
function Compare({ p, field }: any) {
  const cur = sharePct(p, "male", field), prev = sharePct(p.prior, "male", field);
  const dMale = Math.round(cur - prev);
  const up = dMale > 0, flat = dMale === 0;
  const col = flat ? C.faint : up ? C.blue : C.pink;
  const curAge = p.headline?.ageRange, prevAge = p.prior.headline?.ageRange;
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid " + C.line }}>
      <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: C.muted }}><b style={{ color: C.faint }}>Last year (same window):</b> {p.prior.headline?.text || "no data"}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 8 }}>
        <span style={{ fontFamily: FIGTREE, fontSize: 12, color: C.body }}>Male share: <b style={{ color: C.ink }}>{Math.round(cur)}%</b> <span style={{ color: col, fontWeight: 700 }}>{flat ? "±0" : (up ? "▲" : "▼") + Math.abs(dMale)} pts</span> <span style={{ color: C.faint }}>(was {Math.round(prev)}%)</span></span>
        {curAge && prevAge && <span style={{ fontFamily: FIGTREE, fontSize: 12, color: C.body }}>Core age: <b style={{ color: C.ink }}>{curAge}</b>{curAge !== prevAge ? <span style={{ color: C.faint }}> (was {prevAge})</span> : <span style={{ color: C.faint }}> (unchanged)</span>}</span>}
      </div>
    </div>
  );
}

function AgeTable({ p, kind }: any) {
  const buyLabel = kind === "google_demo" ? "conv." : "buyers";
  return (
    <>
      <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10.5, color: C.faint, margin: "18px 0 8px" }}>By age</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", minWidth: 560, borderCollapse: "collapse", fontFamily: FIGTREE }}>
          <thead>
            <tr>{["Age", buyLabel, `% of ${buyLabel}`, "% of clicks", "Buy rate", kind === "google_demo" ? "Value/conv" : "AOV"].map((h, i) => (
              <th key={h} style={{ textAlign: i === 0 ? "left" : "right", padding: "8px 12px", fontFamily: OSWALD, fontWeight: 700, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: C.faint, borderBottom: "1px solid " + C.line, whiteSpace: "nowrap" }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {(p.age || []).map((a: any) => {
              const buyPct = p.totals?.purchases > 0 ? (a.purchases / p.totals.purchases) * 100 : 0;
              const clkPct = p.totals?.clicks > 0 ? (a.clicks / p.totals.clicks) * 100 : 0;
              const conv = a.clicks > 0 ? (a.purchases / a.clicks) * 100 : 0;
              const aov = a.purchases > 0 ? a.revenue / a.purchases : 0;
              const top = buyPct >= 20;
              return (
                <tr key={a.key}>
                  <td style={{ padding: "10px 12px", fontSize: 13.5, color: C.ink, fontWeight: 600, borderBottom: "1px solid " + C.lineSoft, whiteSpace: "nowrap" }}>{a.key}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 13.5, color: C.body, borderBottom: "1px solid " + C.lineSoft, fontVariantNumeric: "tabular-nums" }}>{int(a.purchases)}</td>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid " + C.lineSoft }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                      <div style={{ flex: 1, maxWidth: 120, height: 7, borderRadius: 999, background: C.cell, overflow: "hidden" }}><div style={{ width: Math.min(100, buyPct) + "%", height: "100%", background: top ? C.green : C.purple }} /></div>
                      <span style={{ minWidth: 44, textAlign: "right", fontSize: 13, fontWeight: top ? 800 : 600, color: top ? C.green : C.body, fontVariantNumeric: "tabular-nums" }}>{pct(buyPct)}</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 13, color: C.muted, borderBottom: "1px solid " + C.lineSoft, fontVariantNumeric: "tabular-nums" }}>{pct(clkPct)}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 13, color: conv >= 2.5 ? C.green : C.body, borderBottom: "1px solid " + C.lineSoft, fontVariantNumeric: "tabular-nums" }}>{pct(conv)}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 13, color: C.body, borderBottom: "1px solid " + C.lineSoft, fontVariantNumeric: "tabular-nums" }}>{money2(aov)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: C.faint, marginTop: 12, lineHeight: 1.5 }}>
        <b style={{ color: C.muted }}>Buy rate</b> = {buyLabel} ÷ clicks for that age. Green rows are ≥20% of {buyLabel}. "Unknown" is people the platform couldn't classify.
      </div>
    </>
  );
}

function GenderBar({ title, sub, rows, field, total, prior, priorTotal }: any) {
  const order = ["male", "female", "unknown"];
  const bar = (rws: any[], tot: number, h: number) => (
    <div style={{ display: "flex", height: h, borderRadius: 6, overflow: "hidden", background: "#0f0b22" }}>
      {order.map((k) => { const v = gv(rws, k, field); const w = tot > 0 ? (v / tot) * 100 : 0; return w <= 0 ? null : <div key={k} title={`${GENDER[k].label} ${w.toFixed(0)}%`} style={{ width: w + "%", background: GENDER[k].color }} />; })}
    </div>
  );
  const legend = (rws: any[], tot: number, faint: boolean) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 9 }}>
      {order.map((k) => { const v = gv(rws, k, field); const w = tot > 0 ? (v / tot) * 100 : 0; return v <= 0 ? null : (
        <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FIGTREE, fontSize: faint ? 11.5 : 12.5, color: faint ? C.muted : C.body }}>
          <span style={{ width: faint ? 8 : 9, height: faint ? 8 : 9, borderRadius: 2, background: GENDER[k].color, opacity: faint ? 0.6 : 1 }} />{GENDER[k].label} <b style={{ color: faint ? C.body : C.ink }}>{w.toFixed(0)}%</b> <span style={{ color: C.faint }}>({int(v)})</span>
        </span>
      ); })}
    </div>
  );
  return (
    <div style={{ background: C.cell, border: "1px solid " + C.line, borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 13, color: C.ink }}>{title}</span>
        <span style={{ fontFamily: FIGTREE, fontSize: 11, color: C.faint }}>{sub}</span>
      </div>
      {bar(rows, total, 26)}
      {legend(rows, total, false)}
      {prior && (
        <div style={{ marginTop: 13, paddingTop: 12, borderTop: "1px solid " + C.lineSoft }}>
          <div style={{ fontFamily: FIGTREE, fontSize: 10.5, fontWeight: 700, color: C.faint, marginBottom: 6, letterSpacing: "0.06em" }}>LAST YEAR</div>
          <div style={{ opacity: 0.8 }}>{bar(prior, priorTotal, 14)}</div>
          {legend(prior, priorTotal, true)}
        </div>
      )}
    </div>
  );
}

// ---- Klaviyo timing card ------------------------------------------------------------
function KlaviyoCard({ isMobile }: any) {
  const s = useSnapshot("klaviyo_timing", "ytd");
  const p = s.snap?.payload;
  const dayRows = p ? [...(p.byDow || [])].filter((d: any) => d.sends > 0).sort((a: any, b: any) => b.rpr - a.rpr) : [];
  const timeRows = p ? [...(p.byHour || [])].filter((h: any) => h.sends > 0).sort((a: any, b: any) => b.ctr - a.ctr) : [];
  return (
    <Card>
      <CardHeader dot={C.green} title="Best time to send · Klaviyo" s={s} />
      {s.err && <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: "#ffb3a3", marginBottom: 12 }}>{s.err}</div>}
      {!p ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: C.muted, padding: "10px 0" }}>
          {s.loading ? "Loading the last saved snapshot…" : "Nothing saved yet. Choose a window (YTD gives the most campaigns) and hit Refresh."}
        </div>
      ) : !p.campaignCount ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: C.muted, padding: "10px 0" }}>No sent email campaigns in this window.</div>
      ) : (
        <>
          {p.reportOk === false && (
            <div style={{ background: "rgba(248,146,92,0.12)", border: "1px solid rgba(248,146,92,0.35)", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontFamily: FIGTREE, fontSize: 12.5, color: "#ffd0ab", lineHeight: 1.5 }}>
              Klaviyo's reporting API was rate-limited on this pull{p.cooldownMin > 0 ? ` (cooling down ~${p.cooldownMin} min)` : ""}, so the revenue &amp; click numbers are missing — the send counts are still real. This is transient (it's why L90 came up empty while the other windows worked); hit <b>Refresh</b> again shortly.
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: isMobile || !(p.topDays?.length > 0) ? "minmax(0,1fr)" : "minmax(0,1fr) minmax(0,1fr)", gap: 16, marginBottom: 18, alignItems: "stretch" }}>
            <div style={{ background: C.cell, border: "1px solid " + C.line, borderRadius: 12, padding: "16px 18px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10.5, color: C.faint, marginBottom: 6 }}>Best send window</div>
              <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: isMobile ? 22 : 28, color: C.green, lineHeight: 1.15 }}>{p.headline?.text}</div>
              <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: C.muted, marginTop: 7 }}>Best day by revenue/recipient · best time by click rate · {WIN_LABEL[p.window] || p.window} · {int(p.campaignCount)} campaigns · {money(p.totals?.revenue)} total</div>
            </div>
            {p.topDays?.length > 0 && (
              <div style={{ background: C.cell, border: "1px solid " + C.line, borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10.5, color: C.faint }}>Recommended send schedule</div>
                <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                  {p.topDays.map((d: any, idx: number) => (
                    <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 11 }}>
                      <span style={{ width: 22, height: 22, flexShrink: 0, borderRadius: 999, background: idx === 0 ? C.green : "transparent", border: "1px solid " + (idx === 0 ? C.green : C.line), color: idx === 0 ? "#160f2e" : C.muted, fontFamily: OSWALD, fontWeight: 800, fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{idx + 1}</span>
                      <span style={{ width: 40, fontFamily: OSWALD, fontWeight: 700, fontSize: 15, color: C.ink }}>{d.key}</span>
                      <span style={{ flex: 1, fontFamily: FIGTREE, fontSize: 12, color: C.faint, whiteSpace: "nowrap" }}>{money(d.revenue)} · {int(d.sends)} sends</span>
                      <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: C.body, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", textAlign: "right" }}><b>${(d.rpr || 0).toFixed(2)}</b><span style={{ color: C.faint, fontWeight: 500 }}>/recip</span></span>
                    </div>
                  ))}
                </div>
                <div style={{ fontFamily: FIGTREE, fontSize: 11, color: C.faint, marginTop: 11, lineHeight: 1.5 }}>Top 3 days by revenue per recipient — #1–2 = your two weekly sends, #3 = a backup{p.bestTime ? <>, all best around <b style={{ color: C.muted }}>{p.bestTime}</b></> : ""}.</div>
              </div>
            )}
          </div>

          <TimingTable title="Days — ranked by revenue per recipient" rows={dayRows} bestKey={p.headline?.bestDay} kind="day" />
          <div style={{ height: 16 }} />
          <TimingTable title="Times — ranked by click rate" rows={timeRows} bestKey={p.headline?.bestTime} kind="time" />

          <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10.5, color: C.faint, margin: "18px 0 8px" }}>Top topics · by revenue</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 520, borderCollapse: "collapse", fontFamily: FIGTREE }}>
              <thead><tr>{["Topic", "Sends", "Revenue", "Rev/recip", "Clicks"].map((h, i) => (
                <th key={h} style={{ textAlign: i === 0 ? "left" : "right", padding: "8px 12px", fontFamily: OSWALD, fontWeight: 700, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: C.faint, borderBottom: "1px solid " + C.line, whiteSpace: "nowrap" }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {(p.topics || []).map((tp: any, idx: number) => (
                  <tr key={tp.key}>
                    <td style={{ padding: "10px 12px", fontSize: 13.5, color: idx === 0 ? C.green : C.ink, fontWeight: idx === 0 ? 800 : 600, borderBottom: "1px solid " + C.lineSoft, whiteSpace: "nowrap" }}>{tp.key}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 13, color: C.muted, borderBottom: "1px solid " + C.lineSoft, fontVariantNumeric: "tabular-nums" }}>{int(tp.sends)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 13.5, color: C.body, fontWeight: 700, borderBottom: "1px solid " + C.lineSoft, fontVariantNumeric: "tabular-nums" }}>{money(tp.revenue)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 13, color: C.body, borderBottom: "1px solid " + C.lineSoft, fontVariantNumeric: "tabular-nums" }}>{"$" + (tp.rpr || 0).toFixed(2)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 13, color: C.muted, borderBottom: "1px solid " + C.lineSoft, fontVariantNumeric: "tabular-nums" }}>{int(tp.uClicks)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: C.faint, marginTop: 12, lineHeight: 1.5 }}>
            <b style={{ color: C.muted }}>Rev/recip</b> = revenue ÷ people it was sent to — normalizes for list size, so one big send can't skew a day. <b style={{ color: C.muted }}>Click rate</b> = unique clickers ÷ delivered, i.e. real engagement (opens are excluded — Apple auto-opens make them unreliable). Everything is cumulative across all sends in the window; times in Mountain.
          </div>
        </>
      )}
    </Card>
  );
}

function TimingTable({ title, rows, bestKey, kind }: any) {
  const cols = kind === "day"
    ? [{ h: "Day", l: true, g: (r: any) => r.key }, { h: "Sends", g: (r: any) => int(r.sends) }, { h: "Revenue", g: (r: any) => money(r.revenue) }, { h: "Click rate", g: (r: any) => (r.ctr || 0).toFixed(1) + "%" }, { h: "Rev/recip", g: (r: any) => "$" + (r.rpr || 0).toFixed(2) }]
    : [{ h: "Time", l: true, g: (r: any) => r.key }, { h: "Sends", g: (r: any) => int(r.sends) }, { h: "Click rate", g: (r: any) => (r.ctr || 0).toFixed(1) + "%" }, { h: "Rev/recip", g: (r: any) => "$" + (r.rpr || 0).toFixed(2) }];
  return (
    <div>
      <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10.5, color: C.faint, marginBottom: 8 }}>{title}</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", minWidth: 480, borderCollapse: "collapse", fontFamily: FIGTREE }}>
          <thead><tr>{cols.map((c: any) => (
            <th key={c.h} style={{ textAlign: c.l ? "left" : "right", padding: "8px 12px", fontFamily: OSWALD, fontWeight: 700, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: C.faint, borderBottom: "1px solid " + C.line, whiteSpace: "nowrap" }}>{c.h}</th>
          ))}</tr></thead>
          <tbody>
            {(rows || []).map((r: any) => { const best = r.key === bestKey; return (
              <tr key={r.key}>{cols.map((c: any, i: number) => (
                <td key={c.h} style={{ textAlign: c.l ? "left" : "right", padding: "10px 12px", fontSize: 13, color: best ? C.green : i === 0 ? C.ink : C.body, fontWeight: best && i === 0 ? 800 : i === 0 ? 600 : 500, borderBottom: "1px solid " + C.lineSoft, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{c.g(r)}</td>
              ))}</tr>
            ); })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ children }: any) {
  return <div style={{ background: C.card, border: "1px solid " + C.line, borderRadius: 16, padding: 22, minWidth: 0, maxWidth: "100%" }}>{children}</div>;
}
